// The player's identity on the board: an anonymous Supabase auth user created on first submission and kept
// in this browser's storage. No email, no password, no profile. Nothing here throws.
import { supabase } from './supabase';
import { timed } from './timeout';

const offline = (): boolean => typeof navigator !== 'undefined' && navigator.onLine === false;

/** The current session's user id without creating one; null when signed out, unconfigured or on error. */
export async function currentUserId(): Promise<string | null> {
  const sb = supabase;
  if (!sb) return null;
  try {
    const { data } = await timed(() => sb.auth.getSession());
    return data.session?.user.id ?? null;
  } catch {
    return null;
  }
}

async function signIn(): Promise<string | null> {
  const sb = supabase;
  if (!sb) return null;
  try {
    const existing = await currentUserId();
    if (existing) return existing;
    if (offline()) return null;
    const { data, error } = await timed(() => sb.auth.signInAnonymously());
    if (error) return null;
    return data.user?.id ?? null;
  } catch {
    return null;
  }
}

let inflight: Promise<string | null> | null = null;

/**
 * The user id to submit under: the existing session, else a fresh anonymous sign-in. Concurrent callers
 * share one sign-in; a failed attempt clears the cache so the next call tries again. Null when the board
 * is unconfigured, the browser is offline, or the sign-in fails.
 */
export function ensureUser(): Promise<string | null> {
  if (inflight) return inflight;
  const attempt = signIn().then((id) => {
    if (id === null) inflight = null;
    return id;
  });
  inflight = attempt;
  return attempt;
}
